import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { getPointOfSales } from '../../services/pointOfSalesService';
import { useSalesPointOfSale } from '../../contexts/SalesPointOfSaleContext';
import { authService } from '../../services/authService';
import { filterPointOfSalesForUser } from '../../utils/limitedUserAccess';
import Toast from '../../components/Common/Toast';

const SelectSalesPointOfSale = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedPointOfSale, setSelectedPointOfSale } = useSalesPointOfSale();
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const returnPath =
    typeof location.state?.from === 'string' && location.state.from.startsWith('/ventas')
      ? location.state.from
      : '/ventas/clients';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getPointOfSales();
        if (!cancelled && res.success) {
          const user = authService.getCurrentUser();
          const active = (res.data || []).filter((p) => p.is_active !== false);
          setPoints(filterPointOfSalesForUser(active, user));
        }
      } catch {
        if (!cancelled) {
          setToast({ isOpen: true, message: 'Error al cargar puntos de venta', type: 'error' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelect = (pos) => {
    setSelectedPointOfSale(pos);
    navigate(returnPath, { replace: true, state: { posConfirmed: true } });
  };

  const filtered = points.filter(
    (p) =>
      !searchTerm ||
      (p.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.code || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Punto de venta</h1>
            <p className="text-gray-600 mt-1 max-w-2xl">
              Use un punto de venta para cotización, orden de servicio, ventas, servicios continuos y documentos de venta.
              Clientes y lista de precios no requieren elegirlo aquí.
            </p>
            {selectedPointOfSale?.id ? (
              <p className="text-sm text-teal-700 mt-2">
                Actual:{' '}
                <strong>
                  {selectedPointOfSale.code ? `${selectedPointOfSale.code} — ` : ''}
                  {selectedPointOfSale.name}
                </strong>
              </p>
            ) : null}
          </div>
          <Link
            to={returnPath}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm font-medium"
          >
            Volver
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-md p-5 mb-6">
          <input
            type="text"
            placeholder="Buscar por código o nombre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        {loading ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-md text-gray-500">Cargando puntos de venta…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-md text-gray-500">
            {searchTerm
              ? 'No se encontraron puntos de venta.'
              : 'No hay puntos de venta activos. Configúrelos en Gestión → Configuración → Puntos de venta.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((pos) => (
              <button
                key={pos.id}
                type="button"
                onClick={() => handleSelect(pos)}
                className="text-left bg-white rounded-lg shadow-md p-6 hover:shadow-lg border border-gray-200 hover:border-teal-300 transition-shadow"
              >
                <h3 className="text-lg font-semibold text-gray-800">{pos.name}</h3>
                {pos.code ? <p className="text-sm font-mono text-teal-600 mt-1">{pos.code}</p> : null}
                <p className="mt-4 text-sm text-teal-600 font-medium">Usar este punto de venta</p>
              </button>
            ))}
          </div>
        )}
      </div>

      <Toast isOpen={toast.isOpen} message={toast.message} type={toast.type} onClose={() => setToast((t) => ({ ...t, isOpen: false }))} />
    </div>
  );
};

export default SelectSalesPointOfSale;
